import { useMutation } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';

/**
 * `POST /api/Master/ValidatePhoneNumber`
 *
 * Carrier validation for a phone number, via our own backend.
 *
 * ## This used to call Twilio from the browser, and must never do so again
 *
 * The previous version of this file called
 * `https://lookups.twilio.com/v1/PhoneNumbers/<E.164>` directly with an
 * `Authorization: Basic` header it built itself from
 * `VITE_TWILIO_ACCOUNT_SID` / `VITE_TWILIO_AUTH_TOKEN` — **and it carried
 * hardcoded literal fallbacks for both**. Vite inlines `VITE_*` into the
 * client bundle at build time, so the Twilio account SID and auth token
 * shipped in the JavaScript to every user, in every environment, whether or
 * not the env vars were set (PROD-2092, PROD-2094).
 *
 * Anything the browser can send, a user can read. The credential belongs on
 * the server, which is the only place it can be kept.
 *
 * DO NOT reintroduce a client-side call here, and do not add a `VITE_*`
 * variable for a secret. If this endpoint is missing something, extend the
 * endpoint.
 *
 * ## The number goes in the BODY
 *
 * A patient's phone number is PHI, and PHI in a URL lands in ingress logs,
 * access logs and browser history — PROD-2060 is that exact failure with
 * access tokens. Hence POST with a body rather than a GET with the number in
 * the path, which is what the Twilio call did.
 *
 * ## Failure behaviour is unchanged
 *
 * The endpoint always answers 200 for a well-formed request and distinguishes
 * "this number is not valid" from "we could not check". A failed *check*
 * throws here, exactly as the old non-OK branch did, so the existing
 * consumers keep showing their field-inline "Could not validate phone number"
 * message and the user can still save. A Twilio outage must never block a
 * patient record.
 */

interface ValidatePhoneNumberResponse {
  valid: boolean;
  /** Canonical E.164 formatting, when the lookup succeeded. */
  formatted?: string | null;
  /** ISO-3166 country code the lookup inferred ("AE", "US"). */
  countryCode?: string | null;
  /**
   * True when the lookup could not be performed at all — Twilio unreachable,
   * erroring, or not configured. Deliberately separate from `valid`: telling a
   * user their correct number is invalid because a third party had a bad
   * minute is worse than telling them we could not check.
   */
  lookupUnavailable?: boolean;
}

interface ValidatePhoneParams {
  /** Full phone with country code, e.g. "+971501234567". */
  contactNumber: string;
}

export interface ValidatePhoneResult {
  valid: boolean;
  /** Canonical formatting if the lookup succeeded. */
  formatted?: string;
  countryCode?: string;
}

export function useValidatePhoneNumber() {
  return useMutation<ValidatePhoneResult, Error, ValidatePhoneParams>({
    // Phone validation surfaces a field-inline error
    // ("Could not validate phone number") in `create-contact-view`. Opt
    // out of the global toast so users don't get both messages on an outage.
    meta: { skipGlobalError: true },
    mutationFn: async (params) => {
      const data = await post<ValidatePhoneNumberResponse>(
        '/api/Master/ValidatePhoneNumber',
        { phoneNumber: params.contactNumber },
      );

      // Could not check. Thrown rather than returned so consumers take their
      // existing error path — the same shape the old `!res.ok` branch had.
      if (data?.lookupUnavailable) {
        throw new Error('Phone number lookup is unavailable');
      }

      if (!data?.valid) {
        return { valid: false };
      }

      return {
        valid: true,
        formatted: data.formatted ?? undefined,
        countryCode: data.countryCode ?? undefined,
      };
    },
  });
}
